import { useCallback } from 'react';
import classnames from 'classnames';

import SpinnerSmall from 'shared/ui/Spinner/SpinnerSmall';
import GaugeSelector from 'shared/ui/GaugeSelector';
import { Checkbox } from 'shared/ui/Checkbox/Checkbox';

import styles from './GaugeTreeOption.module.scss';
import { useToggleSelection } from './hooks/useToggleSelection';

export type GaugeSelectorOptionPropType = {
  value: number;
  label: string;
  description?: string;
};

type GaugeTreeOptionType = {
  onChange: (options: GaugeTreeOptionType['selectedOptions']) => void;
  getOptionId: (option: GaugeTreeOptionType['option']) => any;
  getOptionLabel: (option: GaugeTreeOptionType['option'], props: GaugeTreeOptionType) => any;
  getOptionValueForGauge: (option: GaugeTreeOptionType['option']) => any;
  setOptionValueFromGauge: (option: GaugeTreeOptionType['option'], value: number | null) => any;
  getOptionDescription: (option: GaugeTreeOptionType['option'], props: GaugeTreeOptionType) => any;
  isUpdatingEntity: boolean;
  option?: { [key: string]: any };
  visible?: boolean;
  disabled?: boolean;
  selectedOptions?: { [key: string]: any }[];
  gaugeSelectorOptions?: (GaugeSelectorOptionPropType | { [key: string]: any })[];
};

export const GaugeTreeOption: React.FC<GaugeTreeOptionType> = (props) => {
  const {
    option,
    visible,
    disabled,
    selectedOptions = [],
    gaugeSelectorOptions,
    onChange,
    getOptionId,
    getOptionLabel = (_option, _props) => null,
    getOptionDescription = (_option, _props) => null,
    getOptionValueForGauge,
    setOptionValueFromGauge,
    isUpdatingEntity,
  } = props;

  const currentOption =
    selectedOptions.find((selectedOption) => getOptionId(selectedOption) === getOptionId(option)) || option;

  const value = getOptionValueForGauge(currentOption);
  const { isSelected, toggleIsSelected } = useToggleSelection(value);

  const resetCurrentOption = useCallback(
    (currentOption) => {
      const filteredSelectOptions = selectedOptions.filter(
        (selectedOption) => getOptionId(selectedOption) !== getOptionId(currentOption),
      );
      onChange(filteredSelectOptions);
    },
    [getOptionId, onChange, selectedOptions],
  );

  const handleChange = useCallback(
    (gaugeValue) => {
      if (disabled) {
        return;
      }

      const optionIndex = selectedOptions.findIndex(
        (selectedOption) => getOptionId(selectedOption) === getOptionId(option),
      );

      if (gaugeValue === getOptionValueForGauge(selectedOptions[optionIndex])) {
        resetCurrentOption(selectedOptions[optionIndex]);
        return;
      }

      const nextOption = setOptionValueFromGauge({ ...option }, gaugeValue);

      const nextSelectedOptions =
        optionIndex >= 0
          ? [...selectedOptions.slice(0, optionIndex), nextOption, ...selectedOptions.slice(optionIndex + 1)]
          : [...selectedOptions, nextOption];
      onChange(nextSelectedOptions);
    },
    [
      disabled,
      option,
      selectedOptions,
      onChange,
      getOptionId,
      getOptionValueForGauge,
      setOptionValueFromGauge,
      resetCurrentOption,
    ],
  );

  const toggleCheckbox = () => {
    if (isSelected) {
      resetCurrentOption(option);
      toggleIsSelected();
    } else {
      handleChange(null);
      toggleIsSelected();
    }
  };

  if (!visible) {
    return null;
  }

  if (!option) {
    return null;
  }

  const id = getOptionId(option);

  const inputId = `tree-select-option-${id}`;

  const label = getOptionLabel(option, props);

  const description = getOptionDescription(option, props);

  const className = classnames(styles.gaugeTreeOption, {
    'tree-menu__option_gauge': true,
    'tree-menu__option_hidden': !visible,
    [styles.gaugeTreeOptionDisabled]: disabled,
    [styles.gaugeTreeOption]: !disabled,
  });

  return (
    <div className={className}>
      <div
        className={classnames({
          [styles.wrapperCheckbox]: !disabled,
        })}
      >
        <div className="angle margin-right" />
        {!disabled ? (
          <Checkbox
            label={label}
            checked={isSelected}
            onChange={toggleCheckbox}
            inputId={inputId}
            labelClassName="tree-menu__option-label"
          />
        ) : (
          label
        )}
      </div>

      {description && <div dangerouslySetInnerHTML={{ __html: description }} />}

      {isUpdatingEntity ? (
        <SpinnerSmall />
      ) : (
        <GaugeSelector
          id={inputId}
          value={value}
          disabled={disabled}
          className="form-control"
          // @ts-expect-error
          options={gaugeSelectorOptions}
          onChange={handleChange}
        />
      )}
    </div>
  );
};
